import { Close } from "@mui/icons-material";
import {
	Avatar,
	Box,
	Chip,
	IconButton,
	InputAdornment,
	MenuItem,
	type Theme,
	Typography,
	useTheme,
} from "@mui/material";
import {
	type MRT_ColumnDef,
	useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { type Control, useFieldArray, useWatch } from "react-hook-form";

import { CommissionDistributionTypeTooltip } from "CommissionDistribution/Components/CommissionDistributionTypeTooltip";
import {
	type ConsultantOption,
	ParticipantType,
} from "CommissionDistribution/Models/types";
import {
	CommissionDistributionResource,
	useCommissionDistributionResource,
} from "CommissionDistribution/Translations/CommissionDistributionResource";
import type { CommissionDistributionFormValues } from "CommissionDistribution/Utils/commissionDistributionSchema";
import { FormTextField } from "Forms/Components/FormTextField";
import { getDefaultTableOptions } from "Shared/DataTable/MaterialReactTable";

type Args = {
	control: Control<CommissionDistributionFormValues>;
	consultants: ConsultantOption[];
	realizator: ConsultantOption | null;
};

type Row =
	| { kind: "realizator"; id: string }
	| { kind: "participant"; id: string; index: number };

export const useCommissionDistributionTable = ({
	control,
	consultants,
	realizator,
}: Args) => {
	const { t } = useCommissionDistributionResource();
	const R = CommissionDistributionResource;
	const theme = useTheme();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "participants",
	});

	const participants = useWatch({ control, name: "participants" }) ?? [];
	const nonRealizatorSum = participants.reduce(
		(acc, p) => acc + (Number(p.amount) || 0),
		0,
	);
	const isOverLimit = nonRealizatorSum > 100;

	const realizatorConsultantId = realizator?.consultantId;

	const typeOptions = useMemo(
		() => [
			{
				code: ParticipantType.ContractTipster,
				name: t(R.Types.ContractTipster.Name),
			},
			{
				code: ParticipantType.ClientTipster,
				name: t(R.Types.ClientTipster.Name),
			},
			{
				code: ParticipantType.InvitedSpecialist,
				name: t(R.Types.InvitedSpecialist.Name),
			},
		],
		[t],
	);

	const rows = useMemo<Row[]>(
		() => [
			{ kind: "realizator", id: "realizator" },
			...fields.map((f, index) => ({
				kind: "participant" as const,
				id: f.id,
				index,
			})),
		],
		[fields],
	);

	const columns = useMemo<MRT_ColumnDef<Row>[]>(
		() => [
			{
				accessorKey: "participantType",
				header: t(R.Columns.ParticipationType),
				Header: () => (
					<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
						{t(R.Columns.ParticipationType)}
						<CommissionDistributionTypeTooltip />
					</Box>
				),
				Cell: ({ row }) => {
					const original = row.original;
					if (original.kind === "realizator") {
						return <RealizatorTypeCell theme={theme} />;
					}
					return (
						<ParticipantTypeCell
							control={control}
							index={original.index}
							placeholder={t(R.Dialog.ParticipantTypePlaceholder)}
							typeOptions={typeOptions}
						/>
					);
				},
			},
			{
				accessorKey: "consultant",
				header: t(R.Columns.Consultant),
				size: 320,
				minSize: 280,
				Cell: ({ row }) => {
					const original = row.original;
					if (original.kind === "realizator") {
						return (
							<RealizatorConsultantCell theme={theme} realizator={realizator} />
						);
					}
					return (
						<ConsultantCell
							control={control}
							index={original.index}
							consultants={consultants}
							realizatorConsultantId={realizatorConsultantId}
							placeholder={t(R.Dialog.ConsultantPlaceholder)}
						/>
					);
				},
			},
			{
				accessorKey: "share",
				header: t(R.Columns.Share),
				muiTableHeadCellProps: { align: "right" },
				muiTableBodyCellProps: { align: "right" },
				Cell: ({ row }) => {
					const original = row.original;
					if (original.kind === "realizator") {
						return (
							<RealizatorShareCell
								control={control}
								theme={theme}
								autoNote={t(R.Dialog.RealizatorAutoNote)}
							/>
						);
					}
					return (
						<FormTextField
							control={control}
							name={`participants.${original.index}.amount`}
							type="number"
							size="small"
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">%</InputAdornment>
									),
								},
							}}
						/>
					);
				},
			},
			{
				accessorKey: "actions",
				header: "",
				muiTableHeadCellProps: { align: "right" },
				muiTableBodyCellProps: { align: "right" },
				Cell: ({ row }) => {
					const original = row.original;
					if (original.kind === "realizator") return null;
					return (
						<IconButton
							size="small"
							onClick={() => remove(original.index)}
							aria-label={t(R.Dialog.RemoveAriaLabel)}
						>
							<Close fontSize="small" />
						</IconButton>
					);
				},
			},
		],
		[
			t,
			theme,
			control,
			consultants,
			typeOptions,
			realizator,
			realizatorConsultantId,
			remove,
		],
	);

	const table = useMaterialReactTable({
		...getDefaultTableOptions<Row>(),
		columns,
		data: rows,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		enablePagination: false,
		enableBottomToolbar: false,
		enableDensityToggle: false,
		enableHiding: false,
		enableFullScreenToggle: false,
		getRowId: (row) => row.id,
		muiTableBodyRowProps: ({ row }) =>
			row.original.kind === "realizator"
				? { sx: { bgcolor: theme.palette.action.hover } }
				: {},
		muiTableProps: { size: "small" },
	});

	return {
		table,
		fields,
		append,
		isOverLimit,
		nonRealizatorSum,
	};
};

type RealizatorTypeCellProps = { theme: Theme };

const RealizatorTypeCell = ({ theme }: RealizatorTypeCellProps) => {
	const { t } = useCommissionDistributionResource();
	const R = CommissionDistributionResource;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Chip
				label={t(R.Dialog.Realizator)}
				size="small"
				sx={{
					bgcolor: theme.palette.action.disabled,
					color: theme.palette.getContrastText(theme.palette.action.disabled),
					fontWeight: 800,
					fontSize: 10,
					textTransform: "uppercase",
					letterSpacing: 0.4,
				}}
			/>
			<Chip
				label={t(R.Dialog.RealizatorRequired)}
				size="small"
				variant="outlined"
				sx={{
					fontSize: 10,
					textTransform: "uppercase",
					letterSpacing: 0.3,
				}}
			/>
		</Box>
	);
};

type ParticipantTypeCellProps = {
	control: Control<CommissionDistributionFormValues>;
	index: number;
	placeholder: string;
	typeOptions: { code: string; name: string }[];
};

const ParticipantTypeCell = ({
	control,
	index,
	placeholder,
	typeOptions,
}: ParticipantTypeCellProps) => (
	<FormTextField
		control={control}
		name={`participants.${index}.participantType`}
		placeholder={placeholder}
		select
		fullWidth
		size="small"
	>
		{typeOptions.map((option) => (
			<MenuItem key={option.code} value={option.code}>
				{option.name}
			</MenuItem>
		))}
	</FormTextField>
);

type RealizatorConsultantCellProps = {
	theme: Theme;
	realizator: ConsultantOption | null;
};

const RealizatorConsultantCell = ({
	theme,
	realizator,
}: RealizatorConsultantCellProps) => {
	const initials = getInitials(realizator?.fullName);
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Avatar
				sx={{
					width: 30,
					height: 30,
					fontSize: 11,
					bgcolor: theme.palette.action.disabled,
					color: theme.palette.getContrastText(theme.palette.action.disabled),
				}}
			>
				{initials}
			</Avatar>
			<Box>
				<Typography variant="body2" fontWeight={700}>
					{realizator?.fullName ?? "—"}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{[realizator?.position, realizator?.division]
						.filter(Boolean)
						.join(" · ")}
				</Typography>
			</Box>
		</Box>
	);
};

type ConsultantCellProps = {
	control: Control<CommissionDistributionFormValues>;
	index: number;
	consultants: ConsultantOption[];
	realizatorConsultantId: string | undefined;
	placeholder: string;
};

const ConsultantCell = ({
	control,
	index,
	consultants,
	realizatorConsultantId,
	placeholder,
}: ConsultantCellProps) => {
	const participants = useWatch({ control, name: "participants" }) ?? [];
	const currentValue = participants[index]?.consultantId;
	const otherUsedConsultantIds = participants
		.map((p, i) => (i === index ? null : p.consultantId))
		.filter((id): id is string => !!id);
	const excluded = realizatorConsultantId
		? [...otherUsedConsultantIds, realizatorConsultantId]
		: otherUsedConsultantIds;
	const consultantOptions = consultants.filter(
		(c) =>
			!excluded.includes(c.consultantId) || c.consultantId === currentValue,
	);
	return (
		<FormTextField
			control={control}
			name={`participants.${index}.consultantId`}
			placeholder={placeholder}
			select
			size="small"
			fullWidth
		>
			{consultantOptions.map((option) => {
				const suffix = option.position ? ` — ${option.position}` : "";
				return (
					<MenuItem key={option.consultantId} value={option.consultantId}>
						{`${option.fullName}${suffix}`}
					</MenuItem>
				);
			})}
		</FormTextField>
	);
};

type RealizatorShareCellProps = {
	control: Control<CommissionDistributionFormValues>;
	theme: Theme;
	autoNote: string;
};

const RealizatorShareCell = ({
	control,
	theme,
	autoNote,
}: RealizatorShareCellProps) => {
	const participants = useWatch({ control, name: "participants" }) ?? [];
	const sum = participants.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
	const isOverLimit = sum > 100;
	const share = Math.max(0, 100 - sum);
	return (
		<>
			<Typography
				variant="body2"
				sx={{
					fontWeight: 700,
					color: isOverLimit ? theme.palette.error.main : undefined,
				}}
			>
				{isOverLimit ? "!" : `${share} %`}
			</Typography>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block" }}
			>
				{autoNote}
			</Typography>
		</>
	);
};

const getInitials = (fullName?: string): string => {
	if (!fullName) return "?";
	const parts = fullName.trim().split(/\s+/);
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
};
